import { ChevronDown, FileText, MessageSquareText, Plus, Sparkles } from 'lucide-react';
import { useState } from 'react';
import Button from '../../../components/Button';
import EmptyState from '../../../components/EmptyState';
import LoadingSpinner from '../../../components/LoadingSpinner';
import type { LessonPage, QuestionOption } from '../services/learningTypes';
import { isQuestionPage } from '../services/learningTypes';
import PageEditor from './PageEditor';
import QuestionPageEditor from './QuestionPageEditor';

interface LessonPagesManagerProps {
    pages: LessonPage[];
    loading: boolean;
    onAddPage: (pageType: 'page' | 'question') => void;
    onUpdatePage: (index: number, field: string, value: string | number | boolean | QuestionOption[] | string[]) => void;
    onDeletePage: (index: number) => void;
    onSavePage?: (index: number) => Promise<void>;
    onConvertPageType?: (index: number) => void;
    pageSaveStates?: Record<number, 'saved' | 'unsaved' | 'saving' | 'error'>;
}

export default function LessonPagesManager({
    pages,
    loading,
    onAddPage,
    onUpdatePage,
    onDeletePage,
    onSavePage,
    onConvertPageType,
    pageSaveStates
}: LessonPagesManagerProps) {
    const [activeTab, setActiveTab] = useState(0);
    const [showAddDropdown, setShowAddDropdown] = useState(false);

    if (loading) {
        return <LoadingSpinner message="Loading lesson pages..." />;
    }

    const handleDeletePage = (index: number) => {
        // If we're deleting the active tab and it's not the first tab, move to previous tab
        if (index === activeTab && activeTab > 0) {
            setActiveTab(activeTab - 1);
        }
        // If we're deleting a tab before the active tab, adjust active tab index
        else if (index < activeTab) {
            setActiveTab(activeTab - 1);
        }
        // If we're deleting the last tab and it's active, move to the new last tab
        else if (index === activeTab && index === pages.length - 1 && pages.length > 1) {
            setActiveTab(pages.length - 2);
        }

        onDeletePage(index);
    };

    const handleAddPage = (pageType: 'page' | 'question') => {
        onAddPage(pageType);
        setShowAddDropdown(false);
        // Switch to the new tab (will be at the end)
        setActiveTab(pages.length);
    };

    const getPageTypeIcon = (page: LessonPage) => {
        return isQuestionPage(page) ? <MessageSquareText size={14} /> : <FileText size={14} />;
    };

    const getPageTypeLabel = (page: LessonPage) => {
        return isQuestionPage(page) ? 'Q' : 'P';
    };

    const AI_BLOCK_MARKER = '{{ai:';
    const hasAiContent = (page: LessonPage) => page.content.includes(AI_BLOCK_MARKER);

    const getTabClassName = (page: LessonPage, isActive: boolean) => {
        const ai = hasAiContent(page);
        if (isActive && ai) return 'whitespace-nowrap flex items-center gap-2 !bg-purple-100 !text-purple-700';
        if (!isActive && ai) return 'whitespace-nowrap flex items-center gap-2 !text-purple-600 hover:!bg-purple-50';
        return 'whitespace-nowrap flex items-center gap-2';
    };

    // Calculate page counts
    const normalPagesCount = pages.filter(page => !isQuestionPage(page)).length;
    const questionPagesCount = pages.filter(page => isQuestionPage(page)).length;
    const aiPagesCount = pages.filter(hasAiContent).length;

    return (
        <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-3">
                    <h4 className="text-lg font-medium text-gray-900">Lesson Content Pages</h4>
                    {pages.length > 0 && (
                        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                                <FileText size={12} />
                                P: {normalPagesCount}
                            </span>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                                <MessageSquareText size={12} />
                                Q: {questionPagesCount}
                            </span>
                            {aiPagesCount > 0 && (
                                <>
                                    <span>•</span>
                                    <span className="flex items-center gap-1 text-purple-600">
                                        <Sparkles size={12} />
                                        AI: {aiPagesCount}
                                    </span>
                                </>
                            )}
                            <span className="text-gray-400">({pages.length} total)</span>
                        </div>
                    )}
                </div>
                <div className="relative">
                    <Button
                        type="button"
                        onClick={() => setShowAddDropdown(!showAddDropdown)}
                        variant="success"
                        size="md"
                        className="flex items-center gap-2"
                    >
                        <Plus size={16} />
                        Add Page
                        <ChevronDown size={16} />
                    </Button>

                    {showAddDropdown && (
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                            <div className="py-1">
                                <button
                                    onClick={() => handleAddPage('page')}
                                    className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                    <FileText size={16} />
                                    Regular Page
                                </button>
                                <button
                                    onClick={() => handleAddPage('question')}
                                    className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                    <MessageSquareText size={16} />
                                    Question Page
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Click outside to close dropdown */}
            {showAddDropdown && (
                <div
                    className="fixed inset-0 z-0"
                    onClick={() => setShowAddDropdown(false)}
                />
            )}

            {pages.length === 0 ? (
                <EmptyState
                    title="No content pages yet"
                    description="Add pages to create the lesson content."
                    icon={<FileText size={48} className="mx-auto text-gray-400" />}
                    action={{
                        label: "Add First Page",
                        onClick: () => setShowAddDropdown(true),
                        variant: "primary"
                    }}
                    className="bg-gray-50 rounded-lg"
                />
            ) : (
                <div className="space-y-4">
                    {/* Tab Navigation */}
                    <nav className="flex flex-wrap p-2 gap-2 overflow-x-auto">
                        {pages.map((page, index) => (
                            <Button
                                key={index}
                                variant={activeTab === index ? 'tab-active' : 'tab'}
                                size="sm"
                                onClick={() => setActiveTab(index)}
                                className={getTabClassName(page, activeTab === index)}
                            >
                                {getPageTypeIcon(page)}
                                <span className={`font-mono text-xs px-1 rounded ${hasAiContent(page) ? 'bg-purple-200/60' : 'bg-gray-100'}`}>
                                    {getPageTypeLabel(page)}
                                </span>
                                {index + 1}
                                {hasAiContent(page) && (
                                    <Sparkles size={12} className="text-purple-500" />
                                )}
                                {pageSaveStates?.[index] === 'unsaved' && (
                                    <span className="ml-1 text-xs text-gray-400">●</span>
                                )}
                            </Button>
                        ))}
                    </nav>

                    {/* Tab Content */}
                    <div className="mt-4">
                        {pages.map((page, index) => (
                            <div
                                key={index}
                                className={activeTab === index ? 'block' : 'hidden'}
                            >
                                {isQuestionPage(page) ? (
                                    <QuestionPageEditor
                                        page={page}
                                        pageNumber={index + 1}
                                        onUpdate={(field, value) => onUpdatePage(index, field, value)}
                                        onDelete={() => handleDeletePage(index)}
                                        onSave={onSavePage ? () => onSavePage(index) : undefined}
                                        onConvertType={onConvertPageType ? () => onConvertPageType(index) : undefined}
                                        saveStatus={pageSaveStates?.[index]}
                                    />
                                ) : (
                                    <PageEditor
                                        page={page}
                                        pageNumber={index + 1}
                                        onUpdate={(field, value) => onUpdatePage(index, field, value)}
                                        onDelete={() => handleDeletePage(index)}
                                        onSave={onSavePage ? () => onSavePage(index) : undefined}
                                        onConvertType={onConvertPageType ? () => onConvertPageType(index) : undefined}
                                        saveStatus={pageSaveStates?.[index]}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
} 